import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';
import { mgGffpPath, mgGffpToFormControlConfig } from './gffp.util';
import { FORMS_TOKEN, FormsConfig } from '../forms/forms.models';
import { Labels, RenderableComponents, SdfCardLabels } from '../app.models';
import { GffpData, GffpFieldPath } from './gffp.models';
import { FormsComponent } from '../forms/forms.component';
import { Subscription } from 'rxjs';
import { EnumsFields } from '../store/enumeration/enumeration.model';
import { Error } from '../store/transaction';
import { isSdfCardFlow } from '../config/app-config';
import { Store } from '@ngrx/store';
import {
  getCMSLabelsandCountriesInfo,
  getCountryDetails,
} from '../store/countries';
import { defaultCountry, isUpdateIdEnabled } from '../store/service-options/service-options.selectors';

@Component({
    selector: 'app-gffp',
    templateUrl: './gffp.component.html',
    styleUrls: ['./gffp.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class GffpComponent
  implements OnInit, OnChanges, AfterViewInit, OnDestroy
{
  precaria: {
    flag: boolean;
    label: string;
    control: { name: string; optionValue: string };
  } = {
    flag: false,
    control: {
      name: 'sender-primaryIdentification-typeCode',
      optionValue: 'ALN',
    },
    label: 'Alien ID/Precaria',
  };
  isSdfCardFlow: boolean;
  @Input() sendValResponseError: Error[] | null;
  private _fieldNames: string[] = [];
  isChildFormInitialized: boolean;
  sdfCardLabels: SdfCardLabels;
  @Input() enumerations: EnumsFields[] = [];
  @Input() locale!: string;
  private currentDefaultCountry: string = '';
  private defaultCountrySub: Subscription | undefined;
  private currentIsUpdateIdEnabled: boolean = false;
  private isUpdateIdEnabledSub: Subscription | undefined;
  @Input() public set fieldNames(names: string[]) {
    this._fieldNames = names;

    if (this._fieldNames?.length > 0) {
      this._buildForm();
    }
  }
  public get fieldNames() {
    return this._fieldNames;
  }

  public _labels: any;
  @Input() public set labels(labels: Labels) {
    this._labels = labels;
    this.sdfCardLabels = labels.stellar.sdfcard;
  }
  public get labels() {
    return this._labels;
  }

  private _countries?: any;
  @Input() public set countries(countries: any) {
    this._countries = countries;
  }

  public get countries(): any {
    return this._countries;
  }

  @Output() public submitted: EventEmitter<GffpData> =
    new EventEmitter<GffpData>();

  @ViewChildren(FormsComponent)
  public childForm?: QueryList<FormsComponent>;

  public get isInvalid() {
    const isFieldNamesPresent = this.fieldNames.length > 0;
    // const isFormInValid = this.childForm?.some(child => child.form && child.form.invalid)!
    const isFormInValid = !!this.childForm?.find((child) => child.isInvalid);
    if (!this.isChildFormInitialized && !isFieldNamesPresent) {
      return false;
    } else if (!this.isChildFormInitialized && isFieldNamesPresent) {
      return true;
    } else {
      return isFieldNamesPresent && isFormInValid;
    }
  }

  closeAllAutoCompletePanels() {
    this.childForm?.forEach((form) => form.closeAllAutoCompletePanels());
  }

  public formConfigs: {
    title: string;
    config: FormsConfig;
  }[] = [];

  private _watches: Subscription[] = [];

  constructor(private store: Store<any>) {
    this.isSdfCardFlow = isSdfCardFlow;
  }

  public ngOnInit(): void {
    // Subscribe to defaultCountry selector
    this.defaultCountrySub = this.store.select(defaultCountry).subscribe(country => {
      this.currentDefaultCountry = country;
      // Rebuild form when default country changes to update ID options
      if (this._fieldNames?.length > 0) {
        this._buildForm();
      }
    });
    this._watches.push(this.defaultCountrySub);

    // Subscribe to isUpdateIdEnabled selector
    this.isUpdateIdEnabledSub = this.store.select(isUpdateIdEnabled).subscribe(enabled => {
      this.currentIsUpdateIdEnabled = enabled;
      // Rebuild form when feature flag changes to update ID options
      if (this._fieldNames?.length > 0) {
        this._buildForm();
      }
    });
    this._watches.push(this.isUpdateIdEnabledSub);

    this.store.select(getCountryDetails).subscribe((country) => {
      this.precaria.flag = country?.code === 'ARG' ? true : false;
    });

    this.store.select(getCMSLabelsandCountriesInfo).subscribe((data) => {
      // Fetching latest latest labels anc countries upon lang chnages
      // ToDo - Upon lang UI should not invoke any rest API
      if (
        data?.availCountries?.length > 0 &&
        data?.labels &&
        this._fieldNames?.length > 0
      ) {
        this.labels = data?.labels?.labels;
        this.sdfCardLabels = data?.labels?.labels?.stellar?.sdfcard;
        this.countries = data?.availCountries;
        this._buildForm();
      }
    });
  }

  public ngOnChanges(): void {
    if (
      this.labels.stellar.sdfcard.confirmationPage.nextSteps.idType.ALN_PREC
    ) {
      this.precaria.label =
        this.labels.stellar.sdfcard.confirmationPage.nextSteps.idType.ALN_PREC;
    }
    this._setPrimaryIdFieldErrorFromApiResponse(this.sendValResponseError);
  }

  public ngAfterViewInit(): void {
    this.childForm?.changes.subscribe(
      () => (this.isChildFormInitialized = true)
    );
  }

  public ngOnDestroy(): void {
    this._watches.forEach((subscription) => subscription.unsubscribe());
  }

  public submitGffp(): void {
    let formattedData: GffpData = {};

    this.childForm?.forEach((childForm) => {
      const value1 = childForm.form && childForm.form.value;
      const value = this.getMappingValues(value1, childForm?.controlArray);

      if (value) {
        Object.keys(value).forEach((valueKey) => {
          // <party>-<type>-<field> OR
          // <party>-<field>
          const path = mgGffpPath(valueKey);

          if (!formattedData[path.party]) {
            formattedData[path.party] = {};
          }

          if (path.schema && !formattedData[path.party][path.schema]) {
            formattedData[path.party][path.schema] = {};
          }

          if (path.schema) {
            formattedData[path.party][path.schema][path.field] =
              value[valueKey];
          } else {
            formattedData[path.party][path.field] = value[valueKey];
          }
        });
      }
    });
    this.submitted.emit(
      Object.keys(formattedData).length > 0 ? formattedData : undefined
    );
  }

  getUniqueTitles(configs: any) {
    const titles = configs.map((config: { title: any }) => config.title);
    const filtered = configs.filter(
      ({ title }: any, index: number) => !titles.includes(title, index + 1)
    );
    if (
      filtered?.length === 1 &&
      filtered[0].config.controls.some(
        (control: any) =>
          control?.name && control.name.includes('sender-primaryIdentification')
      )
    ) {
      filtered[0].title = '';
    }
    return filtered;
  }

  getMappingValues(value: any, controls: any) {
    let formFields: any = {}; // Todo: Tim: Fix type

    Object.keys(value).forEach((valueKey) => {
      const data = controls[0]?.filter(
        (control: { inputType: undefined; name: string }) =>
          control.inputType === undefined && control.name === valueKey
      );
      const opt =
        data[0]?.options &&
        data[0]?.options?.find(
          (opt: { label: any }) => opt.label === value[valueKey]
        );

      formFields[valueKey] = opt && opt?.value ? opt.value : value[valueKey];
    });
    return formFields;
  }

  private _buildForm() {
    // TODO -- clean up old renderable component stuff or repurpose
    const baseConfig = {
      component: RenderableComponents.FORM,
      token: FORMS_TOKEN,
    };
    this.formConfigs = this._fieldNames
      .map((fieldName) => mgGffpPath(fieldName))
      // filter for unique sections, unique = `party.schema`
      .filter(
        (path: GffpFieldPath, index: number, self: GffpFieldPath[]) =>
          self.findIndex((selfPath) => {
            if (!selfPath.schema && !path.schema) {
              return selfPath.party === path.party;
            } else {
              return (
                selfPath.party === path.party && selfPath.schema === path.schema
              );
            }
          }) === index
      )
      .map((path) => {
        // checking for <party>.<field> vs <party>.<type>.<field>
        const schemaProperCase =
          path.schema &&
          `${path.schema.charAt(0).toUpperCase()}${path.schema.substr(1)}`;
        return {
          title: this.labels.stellar.gffp.gffpLabels[
            `${path.party}${schemaProperCase || ''}Title`
          ] as string,
          config: {
            ...baseConfig,
            controls: this._fieldNames
              // collect all fields for this party.schema
              .filter(
                (name) =>
                  (schemaProperCase &&
                    name.startsWith(`${path.party}.${path.schema}`)) ||
                  (name.split('.').length === 2 && !path.schema)
              )
              .map((fieldName) =>
                mgGffpToFormControlConfig(
                  fieldName,
                  this.enumerations,
                  this.labels,
                  this.locale,
                  this.countries,
                  this.precaria,
                  this.currentDefaultCountry,
                  this.currentIsUpdateIdEnabled
                )
              )
              .filter((field) => !!field),
          } as FormsConfig,
        };
      })
      .map((fieldsConfig) => {
        if (fieldsConfig && fieldsConfig?.config?.controls[0]) {
          let sortedConfig = fieldsConfig;
          sortedConfig.config.controls.map((control) => {
            if (
              control?.name &&
              control.name === 'sender-primaryIdentification-typeCode' &&
              control?.options
            ) {
              control.options = control.options.sort((a, b) =>
                a.label.toLowerCase().localeCompare(b.label.toLowerCase())
              );
            }
          });
          return sortedConfig;
        } else return fieldsConfig;
      })
      .filter((section) => section.title);
    this.formConfigs = this.getUniqueTitles(this.formConfigs);
  }
  private _setPrimaryIdFieldErrorFromApiResponse(errors: Error[] | null): void {
    if (!errors || errors.find((error) => error)?.category !== 'MAPI-20000')
      return;
    for (let error of errors) {
      const fieldName = error.offendingFields[0]?.field.replace(/\./g, '-');
      for (let formConfig of this.formConfigs) {
        const control = formConfig.config.controls.find(
          (control) => control.name === fieldName
        );
        if (control) {
          this.childForm?.first.renderPrimaryIdFieldErrorMessage(control.name);
        }
      }
    }
  }
}
